import { bindActionCreators } from '@reduxjs/toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import type { Facet } from '../../@types/ElasticSearchResponse';
import { closeFoldout, openFilter } from '../../actions/filters';
import type QueryFilter from '../../helpers/query-filter';
import type { RootState } from '../../reducers';
import { togglableFilters, toggleableExcludingFilters } from '../../shared/togglable-filters';
import {
  getActiveFacet,
  getFacetList,
  getQueryFilter,
  getSearchHitCount,
} from '../../utils/reselectors';
import FilterFoldoutCheckbox from './filter-foldout-checkbox';
import FilterFoldoutItem from './filter-foldout-item';
import FilterWxPanel from './filter-wx-panel';
import SelectedFilterModal from './selected-filter-modal';

interface FilterFoldoutProps {
  onRequestSearch: (queryFilter: QueryFilter) => void;
}

const FilterFoldout = ({ onRequestSearch }: FilterFoldoutProps) => {
  const { t } = useTranslation('search');
  const dispatch = useDispatch();

  const open = useSelector<RootState, boolean>((s) => s.filters.foldoutOpen);
  const current = useSelector(getActiveFacet);
  const facets = useSelector(getFacetList);
  const queryFilter = useSelector(getQueryFilter);
  const numberOfHits = useSelector(getSearchHitCount);

  const actions = bindActionCreators({ openFilter, closeFoldout }, dispatch);

  const onDisplayFacet = (facet: Facet) =>
    actions.openFilter(facet, !process.env.SSR ? window.scrollY : 0, false);

  const onCloseFoldout = () => actions.closeFoldout();

  if (!open) return null;

  let className = undefined;
  if (open && current) {
    className = 'is-open';
  } else if (open) {
    className = 'is-open filter-list-is-open';
  }

  return (
    <div data-wx-filtercontainer="" className={className}>
      <div className="filter-container">
        <div className="filter-container-section-nav">
          <FilterWxPanel
            modifiers="list"
            title={t('All filters', { ns: 'search' })}
            onRequestSearch={onRequestSearch}
          >
            <div className="filter-panel-body-content">
              <div data-wx-filterlist="">
                <ul className="filter-list">
                  {facets
                    .filter(({ hidden }) => !hidden)
                    .map((facet) => (
                      <FilterFoldoutItem
                        key={facet.id}
                        facet={facet}
                        isActive={!!(current && current.id === facet.id)}
                        onClick={() => onDisplayFacet(facet)}
                      />
                    ))}
                  <div data-wx-filtertogglelist="">
                    <ul className="filter-toggle-list">
                      {togglableFilters().map((filter) => (
                        <FilterFoldoutCheckbox
                          key={filter.id}
                          filter={filter}
                          queryFilter={queryFilter}
                          onChange={onRequestSearch}
                        />
                      ))}
                      {toggleableExcludingFilters().map((filter) => (
                        <FilterFoldoutCheckbox
                          key={filter.id}
                          filter={filter}
                          queryFilter={queryFilter}
                          excluding={true}
                          onChange={onRequestSearch}
                        />
                      ))}
                    </ul>
                  </div>
                </ul>
              </div>
            </div>
            <div className="filter-panel-body-footer">
              <div className="filter-panel-body-footer-action-container">
                <div className="filter-panel-body-footer-action">
                  <button
                    type="button"
                    data-wx-button="action narrow full-width"
                    title={t('Show results', { ns: 'search' })}
                    onClick={onCloseFoldout}
                  >
                    <div className="button-icon">
                      <i className="icon-search no-margin" />
                    </div>
                    <div className="button-text">
                      {t('Show number of results', { ns: 'search', count: numberOfHits })}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </FilterWxPanel>
        </div>
        <div className="filter-container-section-content">
          <div className="filter-container-content">
            <SelectedFilterModal onRequestSearch={onRequestSearch} />
          </div>
          <div className="ui-block" onClick={onCloseFoldout} />
        </div>
      </div>
    </div>
  );
};

export default FilterFoldout;
